import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {AssignCaseModal} from "./AssignCaseModal";
import {useAssignAdvocateToCase} from "../../../hooks/useLegalQueries.js"

import {
  Badge, PageHeader, Spinner, SearchInput, EmptyState, ErrorState,
  MetricCard, Modal, InfoRow, Avatar, TopLoader,
} from "../../../Component/Workshop/UI";

import {
  useVendors,
  useCreateVendor,
  useUpdateVendor,
  useAssignVendor,
  useAdminAssignQSCase,
} from "../../../hooks/useVendorQueries";
import {useAssignQSVendor} from "../../../hooks/useCraneQueries.js"

// ─── Constants ────────────────────────────────────────────────────────────────
const VENDOR_TYPE_TABS = [
  { key: "ALL",                 label: "All"              },
  { key: "crane",               label: "Crane"            },
  { key: "recoveryVan",         label: "Recovery Van"     },
  { key: "craneAndRecoveryVan", label: "Crane & Recovery" },
  { key: "advocate",            label: "Advocate"         },
];

const STATUS_TABS = [
  { key: "ALL", label: "All"      },
  { key: "1",   label: "Active"   },
  { key: "0",   label: "Inactive" },
];

const SORT_OPTIONS = [
  { value: "vendorName", label: "Name A–Z"    },
  { value: "vendorCode", label: "Vendor Code" },
  { value: "district",   label: "District"    },
  { value: "addedTime",  label: "Date Added"  },
];

const AGREEMENT_TYPES = ["All", "signed", "unsigned", "pending"];

function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Vendor-specific badge helpers ───────────────────────────────────────────
function StatusBadge({ isActive }) {
  const active = isActive === "1" || isActive === 1 || isActive === true;
  return <Badge variant={active ? "green" : "gray"}>{active ? "Active" : "Inactive"}</Badge>;
}

function VendorTypeBadge({ type }) {
  const map = {
    crane:               { variant: "blue",   label: "Crane"            },
    recoveryVan:         { variant: "teal",   label: "Recovery Van"     },
    craneAndRecoveryVan: { variant: "purple", label: "Crane & Recovery" },
    advocate:            { variant: "orange", label: "Advocate"         },
  };
  const cfg = map[type] || { variant: "gray", label: type || "—" };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

function AgreementBadge({ signed, type }) {
  if (signed) return <Badge variant="green">Signed</Badge>;
  if (type)   return <Badge variant="orange">{type}</Badge>;
  return <Badge variant="gray">—</Badge>;
}

function DocLink({ url, label }) {
  if (!url) return <span style={{ fontSize: 12, color: "var(--wd-t3)" }}>—</span>;
  return (
    <a
      href={url} target="_blank" rel="noreferrer"
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        fontSize: 12, color: "var(--wd-blue)", textDecoration: "none",
        padding: "3px 8px", borderRadius: "var(--wd-radius)",
        border: "0.5px solid var(--wd-blue)",
        background: "rgba(59,130,246,.08)", transition: "opacity .12s",
      }}
    >
      <DocIcon size={12} /> {label}
    </a>
  );
}

function MapBtn({ lat, lng }) {
  if (!lat || !lng) return <span style={{ fontSize: 12, color: "var(--wd-t3)" }}>—</span>;
  const url = `https://maps.google.com?q=${lat},${lng}`;
  return (
    <a
      href={url} target="_blank" rel="noreferrer"
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        fontSize: 12, color: "var(--wd-teal)", textDecoration: "none",
        padding: "3px 8px", borderRadius: "var(--wd-radius)",
        border: "0.5px solid var(--wd-teal)",
        background: "rgba(20,184,166,.08)", transition: "opacity .12s", cursor: "pointer",
      }}
    >
      <PinIcon size={12} /> {Number(lat).toFixed(4)}, {Number(lng).toFixed(4)}
    </a>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function VendorsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const globalSearch = searchParams.get("q") || "";
  const requestCode = searchParams.get("requestCode") || "";
  const serviceType = searchParams.get("serviceType") || "";
  const vendorCodeParam = searchParams.get("vendorCode") || "";

  const tabParam = searchParams.get("tab") || "";
  const [activeTypeTab,   setActiveTypeTab]   = useState(tabParam && VENDOR_TYPE_TABS.some(t => t.key === tabParam) ? tabParam : "ALL");
  const [activeStatusTab, setActiveStatusTab] = useState("ALL");
  const [search,          setSearch]          = useState(globalSearch);
  const [districtFilter,  setDistrictFilter]  = useState("");
  const [agreementFilter, setAgreementFilter] = useState("All");
  const [sortBy,          setSortBy]          = useState("vendorName");
  const [viewMode,        setViewMode]        = useState("grid");

  const [detailVendor, setDetailVendor] = useState(null);
  const [addModal,     setAddModal]     = useState(false);
  const [editModal,    setEditModal]    = useState(null);
  const [assignModal,  setAssignModal]  = useState(null);
  const [form,         setForm]         = useState(emptyForm());

  console.log("assignModal", assignModal, "requestCode", requestCode, "serviceType", serviceType);

  useEffect(() => {
    if (globalSearch !== search) setSearch(globalSearch);
  }, [globalSearch]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value?.trim()) next.set("q", value);
      else next.delete("q");
      return next;
    }, { replace: true });
  };

  const debouncedSearch = useDebounce(search, 400);

  // ── Build query params — all server-side filtering ────────────────────────
  const queryParams = useMemo(() => ({
    search:          debouncedSearch  || undefined,
    vendorType:      activeTypeTab !== "ALL" ? activeTypeTab : undefined,
    isActive:        activeStatusTab !== "ALL" ? activeStatusTab : undefined,
    district:        districtFilter   || undefined,
    agreementFilter: agreementFilter !== "All" ? agreementFilter : undefined,
    sortBy:          sortBy,
  }), [debouncedSearch, activeTypeTab, activeStatusTab, districtFilter, agreementFilter, sortBy]);

  // ── Stable ref so list doesn't blank while refetching ─────────────────────
  const stableDataRef = useRef(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    isFetching,
  } = useVendors(queryParams);

  if (data) stableDataRef.current = data;
  const stableData = data || stableDataRef.current;

  const vendors  = stableData?.pages.flatMap(p => p.vendors) || [];
  const total    = stableData?.pages[0]?.total    ?? 0;
  const rawMetrics = stableData?.pages[0]?.metrics || {};

  const metrics = {
    total:    rawMetrics.total              ?? 0,
    active:   rawMetrics.active             ?? 0,
    crane:    rawMetrics.crane              ?? 0,
    recovery: rawMetrics.recoveryVan        ?? 0,
    both:     rawMetrics.craneAndRecoveryVan ?? 0,
  };

  const linkedVendor = vendorCodeParam
  ? vendors.find(v => v.vendorCode === vendorCodeParam)
  : null;

  useEffect(() => {
    if (linkedVendor) {
      setDetailVendor(linkedVendor);
      // Optional: clean the param from the URL once opened
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("vendorCode");
          return next;
        }, { replace: true });
      }
  }, [linkedVendor]);

  const hasMore  = hasNextPage;
  const loadMore = fetchNextPage;

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();
  const assignQSVendor = useAssignQSVendor();
  const assignAdminQSVendor = useAdminAssignQSCase()
  const assignAdvocate = useAssignAdvocateToCase(requestCode); 

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openAdd  = () => { setForm(emptyForm()); setAddModal(true); };
  const openEdit = (v) => { setForm(vendorToForm(v)); setEditModal(v); };

  const handleSave = async () => {
    try {
      if (editModal) {
        await updateVendor.mutateAsync({ vendorCode: editModal.vendorCode, ...form });
      } else {
        await createVendor.mutateAsync(form);
      }
      setAddModal(false);
      setEditModal(null);
    } catch (err) {
      console.error("Save vendor failed:", err);
    }
  };

   const handleAssign = async (caseNo) => {
    console.log("Assigning case", caseNo, "to vendor", assignModal);
    const isAdvocateFlow = serviceType === "advocate";
  try {
     if (isAdvocateFlow) {
      await assignAdvocate.mutateAsync({
        advocateId: assignModal.vendorCode,
        advocateName: assignModal.vendorName,
        advocateNumber: assignModal.vendorPhone,
      });
    } else {
      await assignAdminQSVendor.mutateAsync({
        requestCode: caseNo,
        vendorCode: assignModal.vendorCode,
      });
    }
    setAssignModal(null);
    
    if (requestCode) {
      // came from a case card, go back
      navigate(
        isAdvocateFlow
          ? `/admin/quick-services/legal-cases/${requestCode}` // adjust to your actual case-detail route
          : `/admin/quick-services?tab=${serviceType}`
      );
    }
  } catch (err) {
    console.error("Assignment failed:", err);
  }
};

  const anyFilter = search || districtFilter || agreementFilter !== "All";

  return (
    <div className="wd-animate" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Header ── */}
      <PageHeader
        title="Vendors"
        subtitle="Manage crane & recovery van vendors"
        actions={
          <button className="wd-btn wd-btn-primary" onClick={openAdd}>
            <PlusIcon /> Add Vendor
          </button>
        }
      />

      {/* ── Metrics ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <MetricCard label="Total Vendors"    value={metrics.total}    color="#3b82f6" />
        <MetricCard label="Active"           value={metrics.active}   color="#22c55e" />
        <MetricCard label="Crane"            value={metrics.crane}    color="#185FA5" />
        <MetricCard label="Recovery Van"     value={metrics.recovery} color="#14b8a6" />
        <MetricCard label="Crane & Recovery" value={metrics.both}     color="#a855f7" />
      </div>

      {/* ── Count row ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 13, color: "var(--wd-t3)" }}>
          Showing <strong style={{ color: "var(--wd-t1)" }}>{vendors.length}</strong>
          {total > vendors.length && (
            <> of <strong style={{ color: "var(--wd-t1)" }}>{total}</strong></>
          )} vendor{vendors.length !== 1 ? "s" : ""}
          {anyFilter && (
            <button
              className="wd-btn wd-btn-ghost wd-btn-sm"
              style={{ marginLeft: 8, fontSize: 11 }}
              onClick={() => { setSearch(""); setDistrictFilter(""); setAgreementFilter("All"); }}
            >
              ✕ Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        {/* Type tabs */}
        <div style={{ display: "flex", gap: 8 }}>
          {VENDOR_TYPE_TABS.map(t => (
            <button
              key={t.key}
              className={`wd-filter-tab ${activeTypeTab === t.key ? "active" : ""}`}
              onClick={() => setActiveTypeTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Controls */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {/* Status toggle */}
          <div style={{
            display: "flex", gap: 2,
            border: "0.5px solid var(--wd-border)",
            borderRadius: "var(--wd-radius)", overflow: "hidden",
          }}>
            {STATUS_TABS.map(t => (
              <button
                key={t.key}
                style={{
                  padding: "5px 10px", fontSize: 12, fontWeight: 500, cursor: "pointer",
                  border: "none",
                  background: activeStatusTab === t.key ? "var(--wd-blue)" : "transparent",
                  color: activeStatusTab === t.key ? "#fff" : "var(--wd-t2)",
                  transition: "all .12s",
                }}
                onClick={() => setActiveStatusTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <SearchInput value={search} onChange={handleSearchChange} placeholder="Name, code, district..." />

          <input
            className="wd-input" style={{ width: 120 }}
            placeholder="District..."
            value={districtFilter} onChange={e => setDistrictFilter(e.target.value)}
          />

          <select
            className="wd-input wd-select" style={{ width: "auto", minWidth: 130 }}
            value={agreementFilter} onChange={e => setAgreementFilter(e.target.value)}
          >
            {AGREEMENT_TYPES.map(a => (
              <option key={a} value={a}>
                {a === "All" ? "All Agreements" : a.charAt(0).toUpperCase() + a.slice(1)}
              </option>
            ))}
          </select>

          <select
            className="wd-input wd-select" style={{ width: "auto", minWidth: 150 }}
            value={sortBy} onChange={e => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          {/* View toggle */}
          <div style={{ display: "flex", gap: 2 }}>
            <button
              className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm"
              style={viewMode === "grid" ? { background: "var(--wd-hover)", color: "var(--wd-t1)" } : {}}
              onClick={() => setViewMode("grid")}
            >
              <GridIcon />
            </button>
            <button
              className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm"
              style={viewMode === "table" ? { background: "var(--wd-hover)", color: "var(--wd-t1)" } : {}}
              onClick={() => setViewMode("table")}
            >
              <ListIcon />
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <ErrorState message="Failed to load vendors" onRetry={() => {}} />
      ) : vendors.length === 0 ? (
        <EmptyState message="No vendors match your filters" />
      ) : (
        <>
          {isFetching && <TopLoader />}
          {viewMode === "grid"
            ? <GridView  vendors={vendors} onDetail={setDetailVendor} onEdit={openEdit} onAssign={setAssignModal} />
            : <TableView vendors={vendors} onDetail={setDetailVendor} onEdit={openEdit} onAssign={setAssignModal} />
          }
        </>
      )}

      {/* ── Load more ── */}
      {hasMore && !isFetchingNextPage && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            className="wd-btn wd-btn-ghost"
            onClick={loadMore}
            style={{ minWidth: 220, justifyContent: "center" }}
          >
            Load more vendors
          </button>
        </div>
      )}

      {/* ── Detail Modal ── */}
      <VendorDetailModal
        vendor={detailVendor}
        onClose={() => setDetailVendor(null)}
        onEdit={() => { openEdit(detailVendor); setDetailVendor(null); }}
        onAssign={() => { setAssignModal(detailVendor); setDetailVendor(null); }}
      />

      {/* ── Add / Edit Modal ── */}
      <VendorFormModal
        open={addModal || !!editModal}
        isEdit={!!editModal}
        form={form}
        setForm={setForm}
        onClose={() => { setAddModal(false); setEditModal(null); }}
        onSave={handleSave}
      />

      {/* ── Assign Case Modal ── */}
      <AssignCaseModal
        vendor={assignModal}
        defaultCaseNo={requestCode}
        defaultServiceType={serviceType}
        onClose={() => setAssignModal(null)}
        onAssign={handleAssign}
      />
    </div>
  );
}

// ─── Grid View ────────────────────────────────────────────────────────────────
function GridView({ vendors, onDetail, onEdit, onAssign }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 16 }}>
      {vendors.map(v => (
        <VendorCard key={v.id} vendor={v} onDetail={onDetail} onEdit={onEdit} onAssign={onAssign} />
      ))}
    </div>
  );
}

// ─── Vendor Card ──────────────────────────────────────────────────────────────
function VendorCard({ vendor: v, onDetail, onEdit, onAssign }) {
  return (
    <div
      className="wd-card"
      style={{ padding: 20, cursor: "pointer", transition: "border-color .15s" }}
      onClick={() => onDetail(v)}
      onMouseEnter={e => e.currentTarget.style.borderColor = "var(--wd-blue)"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "var(--wd-border)"}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Avatar name={v.vendorName} size={46} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--wd-t1)", lineHeight: 1.3 }}>{v.vendorName}</div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 2, fontFamily: "var(--wd-mono)" }}>{v.vendorCode}</div>
            <div style={{ marginTop: 4 }}><VendorTypeBadge type={v.vendorType} /></div>
          </div>
        </div>
        <StatusBadge isActive={v.isActive} />
      </div>

      {/* Contact */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14, fontSize: 12 }}>
        <div style={{ color: "var(--wd-t2)" }}>📞 {v.vendorPhone || "—"}</div>
        <div style={{ color: "var(--wd-t2)" }}>📧 {v.email ? v.email.split("@")[0] + "…" : "—"}</div>
        <div style={{ color: "var(--wd-t2)", gridColumn: "1/-1" }}>📍 {v.district}{v.state ? `, ${v.state}` : ""}</div>
      </div>

      {/* Contact person */}
      {v.contactPerson && (
        <div style={{ fontSize: 12, color: "var(--wd-t3)", marginBottom: 10 }}>
          👤 {v.contactPerson} · {v.contactPersonNum || "—"}
        </div>
      )}

      {/* Agreement */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Agreement:</span>
        <AgreementBadge signed={v.agreementSigned} type={v.typeOfAgreement} />
      </div>

      {/* Location */}
      {(v.latitude || v.longitude) && (
        <div style={{ marginBottom: 12 }}>
          <MapBtn lat={v.latitude} lng={v.longitude} />
        </div>
      )}

      <div style={{ borderTop: "0.5px solid var(--wd-border)", margin: "0 0 12px" }} />

      {/* Actions */}
      <div style={{ display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
        <button
          className="wd-btn wd-btn-ghost wd-btn-sm"
          style={{ flex: 1, justifyContent: "center" }}
          onClick={() => onDetail(v)}
        >
          View Details
        </button>
        <button
          className="wd-btn wd-btn-primary wd-btn-sm"
          onClick={() => onAssign(v)}
        >
          Assign Case
        </button>
        <button
          className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm"
          onClick={() => onEdit(v)} title="Edit"
        >
          <EditIcon />
        </button>
      </div>
    </div>
  );
}

// ─── Table View ───────────────────────────────────────────────────────────────
function TableView({ vendors, onDetail, onEdit, onAssign }) {
  return (
    <div className="wd-card">
      <div className="wd-table-wrap">
        <table className="wd-table">
          <thead>
            <tr>
              <th>VENDOR</th>
              <th>TYPE</th>
              <th>PHONE</th>
              <th>DISTRICT / STATE</th>
              <th>CONTACT PERSON</th>
              <th>AGREEMENT</th>
              <th>LOCATION</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map(v => (
              <tr key={v.id} onClick={() => onDetail(v)}>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar name={v.vendorName} size={32} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{v.vendorName}</div>
                      <div style={{ fontSize: 11, color: "var(--wd-t3)", fontFamily: "var(--wd-mono)" }}>{v.vendorCode}</div>
                    </div>
                  </div>
                </td>
                <td><VendorTypeBadge type={v.vendorType} /></td>
                <td style={{ fontFamily: "var(--wd-mono)", fontSize: 12 }}>{v.vendorPhone || "—"}</td>
                <td>
                  <div style={{ fontSize: 13 }}>{v.district || "—"}</div>
                  <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>{v.state}</div>
                </td>
                <td>
                  <div style={{ fontSize: 13 }}>{v.contactPerson || "—"}</div>
                  <div style={{ fontSize: 11, color: "var(--wd-t3)", fontFamily: "var(--wd-mono)" }}>{v.contactPersonNum}</div>
                </td>
                <td><AgreementBadge signed={v.agreementSigned} type={v.typeOfAgreement} /></td>
                <td>
                  {v.latitude ? (
                    <a
                      href={`https://maps.google.com?q=${v.latitude},${v.longitude}`}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        fontSize: 12, color: "var(--wd-teal)", textDecoration: "none",
                        padding: "3px 8px", borderRadius: "var(--wd-radius)",
                        border: "0.5px solid var(--wd-teal)", background: "rgba(20,184,166,.08)",
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <PinIcon size={11} /> View
                    </a>
                  ) : <span style={{ color: "var(--wd-t3)", fontSize: 12 }}>—</span>}
                </td>
                <td><StatusBadge isActive={v.isActive} /></td>
                <td onClick={e => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 5 }}>
                    <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => onDetail(v)}>View</button>
                    <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => onAssign(v)}>Assign</button>
                    <button className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm" onClick={() => onEdit(v)}><EditIcon /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Vendor Detail Modal ──────────────────────────────────────────────────────
const DETAIL_TABS = ["Overview", "Documents", "Location"];

function VendorDetailModal({ vendor: v, onClose, onEdit, onAssign }) {
  const [tab, setTab] = useState("Overview");
  useEffect(() => { if (v) setTab("Overview"); }, [v]);
  if (!v) return null;

  return (
    <Modal
      open={!!v} onClose={onClose} title="Vendor Details" maxWidth={680}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
          <button className="wd-btn wd-btn-ghost" onClick={onClose}>Close</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="wd-btn wd-btn-ghost" onClick={onEdit}><EditIcon /> Edit</button>
            <button className="wd-btn wd-btn-primary" onClick={onAssign}>Assign Case</button>
          </div>
        </div>
      }
    >
      {/* Identity */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <Avatar name={v.vendorName} size={52} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 17, color: "var(--wd-t1)" }}>{v.vendorName}</span>
            <StatusBadge isActive={v.isActive} />
            <VendorTypeBadge type={v.vendorType} />
          </div>
          <div style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 4, fontFamily: "var(--wd-mono)" }}>{v.vendorCode}</div>
          {v.companyName && <div style={{ fontSize: 12, color: "var(--wd-t2)", marginTop: 2 }}>🏢 {v.companyName}</div>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "0.5px solid var(--wd-border)", margin: "0 -20px", padding: "0 20px" }}>
        {DETAIL_TABS.map(t => (
          <button
            key={t}
            className={`wd-detail-tab ${tab === t ? "active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <>
          <SectionHeading>Contact Information</SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <InfoRow label="Phone"           value={v.vendorPhone} mono />
            <InfoRow label="Email"           value={v.email} />
            <InfoRow label="Contact Person"  value={v.contactPerson} />
            <InfoRow label="Contact Phone 1" value={v.contactPersonNum} mono />
            <InfoRow label="Contact Phone 2" value={v.contactPersonNum2} mono />
            <InfoRow label="Login Mobile"    value={v.login_mobile} mono />
          </div>

          <SectionHeading>Address</SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <InfoRow label="Address"  value={v.address} />
            <InfoRow label="District" value={v.district} />
            <InfoRow label="State"    value={v.state} />
            <InfoRow label="Pincode"  value={v.pincode} mono />
          </div>

          <SectionHeading>Business Details</SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <InfoRow label="Vehicle No"        value={v.vehicleNo} mono />
            <InfoRow label="Rate"              value={v.rate ? `₹${v.rate}` : null} />
            <InfoRow label="Per Hour Rate"     value={v.perHR} />
            <InfoRow label="Type of Agreement" value={v.typeOfAgreement} />
            <InfoRow label="Agreement Signed"  value={v.agreementSigned ? "Yes" : "No"} />
            <InfoRow label="Added On"          value={v.addedTime} />
          </div>

          <SectionHeading>Tax / KYC</SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <InfoRow label="GST No"      value={v.GSTNo} mono />
            <InfoRow label="PAN No"      value={v.panNo} mono />
            <InfoRow label="Aadhaar No"  value={v.adharNo ? v.adharNo.replace(/\d(?=\d{4})/g, "*") : null} mono />
            <InfoRow label="Father Name" value={v.fatherName} />
          </div>

          {v.remark && (
            <>
              <SectionHeading>Remarks</SectionHeading>
              <p style={{ fontSize: 13, color: "var(--wd-t2)", margin: 0 }}>{v.remark}</p>
            </>
          )}
        </>
      )}

      {tab === "Documents" && (
        <>
          <SectionHeading>Uploaded Documents</SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>GST Certificate</span>
              <DocLink url={v.GST} label="GST Doc" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Aadhaar Card</span>
              <DocLink url={v.adharCard} label="Aadhaar" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>PAN Card</span>
              <DocLink url={v.panCard} label="PAN" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Agreement</span>
              <DocLink url={v.agreement} label="Agreement" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Business Registration</span>
              <DocLink url={v.businessRegistration} label="Reg Doc" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Cancelled Cheque</span>
              <DocLink url={v.cancelledCheque} label="Cheque" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Profile Image</span>
              <DocLink url={v.profileImage} label="Photo" />
            </div>
          </div>
          {v.images && (
            <>
              <SectionHeading>Additional Images</SectionHeading>
              <p style={{ fontSize: 12, color: "var(--wd-t3)", margin: 0 }}>
                {Array.isArray(v.images) ? `${v.images.length} image(s) uploaded` : "Images available"}
              </p>
            </>
          )}
        </>
      )}

      {tab === "Location" && (
        <>
          <SectionHeading>Current Location</SectionHeading>
          {v.latitude && v.longitude ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <InfoRow label="Latitude"     value={String(v.latitude)} mono />
                <InfoRow label="Longitude"    value={String(v.longitude)} mono />
                <InfoRow label="Last Updated" value={v.location_updated_at || "—"} />
              </div>
              <a
                href={`https://maps.google.com?q=${v.latitude},${v.longitude}`}
                target="_blank" rel="noreferrer"
                className="wd-btn wd-btn-ghost wd-btn-sm"
                style={{ alignSelf: "flex-start", color: "var(--wd-teal)", borderColor: "var(--wd-teal)" }}
              >
                <PinIcon /> Open in Google Maps
              </a>
              <div style={{
                background: "var(--wd-hover)", border: "0.5px solid var(--wd-border)",
                borderRadius: 12, padding: 20, textAlign: "center",
              }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📍</div>
                <div style={{ fontSize: 13, color: "var(--wd-t1)", fontFamily: "var(--wd-mono)", fontWeight: 600 }}>
                  {Number(v.latitude).toFixed(6)}, {Number(v.longitude).toFixed(6)}
                </div>
                <div style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 4 }}>
                  {v.district}, {v.state}
                </div>
              </div>
            </div>
          ) : (
            <EmptyState message="No location data available for this vendor" />
          )}
        </>
      )}
    </Modal>
  );
}

// ─── Vendor Form Modal ────────────────────────────────────────────────────────
function VendorFormModal({ open, isEdit, form, setForm, onClose, onSave }) {
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <Modal
      open={open} onClose={onClose}
      title={isEdit ? "Edit Vendor" : "Add New Vendor"} maxWidth={620}
      footer={
        <>
          <button className="wd-btn wd-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={onSave}>
            {isEdit ? "Save Changes" : "Add Vendor"}
          </button>
        </>
      }
    >
      {/* Vendor Type */}
      <SectionHeading>Vendor Type</SectionHeading>
      <div style={{ display: "flex", gap: 8 }}>
        {VENDOR_TYPE_TABS.filter(t => t.key !== "ALL").map(t => (
          <button
            key={t.key}
            className="wd-btn wd-btn-ghost wd-btn-sm"
            style={form.vendorType === t.key
              ? { borderColor: "var(--wd-blue)", color: "var(--wd-blue)", background: "rgba(59,130,246,.1)" }
              : {}}
            onClick={() => setForm(f => ({ ...f, vendorType: t.key }))}
          >
            {form.vendorType === t.key && "✓ "}{t.label}
          </button>
        ))}
      </div>

      {/* Basic Info */}
      <SectionHeading>Basic Information</SectionHeading>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FormField label="Vendor Name *">
          <input className="wd-input" placeholder="Full name" value={form.vendorName} onChange={set("vendorName")} />
        </FormField>
        <FormField label="Company Name">
          <input className="wd-input" placeholder="Company / firm name" value={form.companyName} onChange={set("companyName")} />
        </FormField>
        <FormField label="Vendor Phone *">
          <input className="wd-input" placeholder="10-digit number" value={form.vendorPhone}
            onChange={e => { const d = e.target.value.replace(/\D/g, "").slice(0, 10); setForm(f => ({ ...f, vendorPhone: d })); }}
            maxLength={10} inputMode="numeric" />
        </FormField>
        <FormField label="Email">
          <input className="wd-input" type="email" placeholder="Email address" value={form.email} onChange={set("email")} />
        </FormField>
        <FormField label="Vehicle No">
          <input className="wd-input" placeholder="e.g. MH01AB1234" value={form.vehicleNo} onChange={set("vehicleNo")} />
        </FormField>
        <FormField label="Rate (₹)">
          <input className="wd-input" type="number" placeholder="Base rate" value={form.rate} onChange={set("rate")} />
        </FormField>
        <FormField label="Per Hour Rate">
          <input className="wd-input" placeholder="Per hour charge" value={form.perHR} onChange={set("perHR")} />
        </FormField>
      </div>

      {/* Contact Person */}
      <SectionHeading>Contact Person</SectionHeading>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <FormField label="Name">
          <input className="wd-input" placeholder="Contact person" value={form.contactPerson} onChange={set("contactPerson")} />
        </FormField>
        <FormField label="Phone 1">
          <input className="wd-input" placeholder="Primary" value={form.contactPersonNum}
            onChange={e => { const d = e.target.value.replace(/\D/g, "").slice(0, 10); setForm(f => ({ ...f, contactPersonNum: d })); }}
            maxLength={10} inputMode="numeric" />
        </FormField>
        <FormField label="Phone 2">
          <input className="wd-input" placeholder="Secondary" value={form.contactPersonNum2}
            onChange={e => { const d = e.target.value.replace(/\D/g, "").slice(0, 10); setForm(f => ({ ...f, contactPersonNum2: d })); }}
            maxLength={10} inputMode="numeric" />
        </FormField>
      </div>

      {/* Address */}
      <SectionHeading>Address</SectionHeading>
      <FormField label="Full Address *">
        <textarea className="wd-input" rows={2} style={{ height: "auto", padding: "8px 10px" }}
          placeholder="Street address" value={form.address} onChange={set("address")} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <FormField label="District *">
          <input className="wd-input" placeholder="District" value={form.district} onChange={set("district")} />
        </FormField>
        <FormField label="State *">
          <input className="wd-input" placeholder="State" value={form.state} onChange={set("state")} />
        </FormField>
        <FormField label="Pincode">
          <input className="wd-input" placeholder="6-digit pincode" value={form.pincode}
            onChange={e => { const d = e.target.value.replace(/\D/g, "").slice(0, 6); setForm(f => ({ ...f, pincode: d })); }}
            maxLength={6} inputMode="numeric" />
        </FormField>
      </div>

      {/* Agreement */}
      <SectionHeading>Agreement</SectionHeading>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FormField label="Type of Agreement">
          <select className="wd-input wd-select" value={form.typeOfAgreement} onChange={set("typeOfAgreement")}>
            <option value="">Select type</option>
            <option value="signed">Signed</option>
            <option value="unsigned">Unsigned</option>
            <option value="pending">Pending</option>
          </select>
        </FormField>
        <FormField label="Agreement Signed?">
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            {["Yes", "No"].map(s => (
              <button
                key={s}
                className="wd-btn wd-btn-ghost wd-btn-sm"
                style={(s === "Yes") === !!form.agreementSigned
                  ? { borderColor: "var(--wd-blue)", color: "var(--wd-blue)", background: "rgba(59,130,246,.1)" }
                  : {}}
                onClick={() => setForm(f => ({ ...f, agreementSigned: s === "Yes" ? 1 : 0 }))}
              >
                {s}
              </button>
            ))}
          </div>
        </FormField>
      </div>

      {/* Status */}
      <SectionHeading>Status</SectionHeading>
      <div style={{ display: "flex", gap: 8 }}>
        {[["1", "Active"], ["0", "Inactive"]].map(([val, label]) => (
          <button
            key={val}
            className="wd-btn wd-btn-ghost wd-btn-sm"
            style={String(form.isActive) === val
              ? { borderColor: "var(--wd-blue)", color: "var(--wd-blue)", background: "rgba(59,130,246,.1)" }
              : {}}
            onClick={() => setForm(f => ({ ...f, isActive: val }))}
          >
            {String(form.isActive) === val && "✓ "}{label}
          </button>
        ))}
      </div>

      {/* KYC */}
      <SectionHeading>KYC / Tax</SectionHeading>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FormField label="GST No">
          <input className="wd-input" placeholder="GST number" value={form.GSTNo} onChange={set("GSTNo")} />
        </FormField>
        <FormField label="PAN No">
          <input className="wd-input" placeholder="PAN number" value={form.panNo} onChange={set("panNo")} />
        </FormField>
        <FormField label="Aadhaar No">
          <input className="wd-input" placeholder="Aadhaar number" value={form.adharNo} onChange={set("adharNo")} />
        </FormField>
        <FormField label="Father's Name">
          <input className="wd-input" placeholder="Father's name" value={form.fatherName} onChange={set("fatherName")} />
        </FormField>
      </div>

      {/* Remark */}
      <SectionHeading>Remarks</SectionHeading>
      <FormField label="Remark">
        <textarea className="wd-input" rows={2} style={{ height: "auto", padding: "8px 10px" }}
          placeholder="Any notes..." value={form.remark} onChange={set("remark")} />
      </FormField>
    </Modal>
  );
}

// ─── Local helpers ────────────────────────────────────────────────────────────
function SectionHeading({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: "var(--wd-t3)",
      textTransform: "uppercase", letterSpacing: ".06em",
      paddingBottom: 6, borderBottom: "0.5px solid var(--wd-border)",
      marginTop: 4,
    }}>
      {children}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-t2)" }}>{label}</label>
      {children}
    </div>
  );
}

function emptyForm() {
  return {
    vendorName: "", companyName: "", vendorType: "crane", vendorPhone: "", email: "",
    contactPerson: "", contactPersonNum: "", contactPersonNum2: "",
    address: "", district: "", state: "", pincode: "",
    GSTNo: "", panNo: "", adharNo: "", fatherName: "",
    vehicleNo: "", rate: "", perHR: "",
    typeOfAgreement: "", agreementSigned: 0,
    isActive: "1", remark: "",
  };
}

function vendorToForm(v) {
  return {
    vendorName: v.vendorName || "", companyName: v.companyName || "",
    vendorType: v.vendorType || "crane", vendorPhone: v.vendorPhone || "",
    email: v.email || "", contactPerson: v.contactPerson || "",
    contactPersonNum: v.contactPersonNum || "", contactPersonNum2: v.contactPersonNum2 || "",
    address: v.address || "", district: v.district || "",
    state: v.state || "", pincode: v.pincode || "",
    GSTNo: v.GSTNo || "", panNo: v.panNo || "",
    adharNo: v.adharNo || "", fatherName: v.fatherName || "",
    vehicleNo: v.vehicleNo || "", rate: v.rate || "", perHR: v.perHR || "",
    typeOfAgreement: v.typeOfAgreement || "", agreementSigned: v.agreementSigned || 0,
    isActive: String(v.isActive ?? "1"), remark: v.remark || "",
  };
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function PlusIcon()           { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function EditIcon()           { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function GridIcon()           { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>; }
function ListIcon()           { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>; }
function DocIcon({ size=13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>; }
function PinIcon({ size=13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>; }